import { useState, useMemo, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Input,
  Select,
  Space,
  Typography,
  Tag,
  Avatar,
  Dropdown,
  Row,
  Col,
  Empty,
  Grid,
  Badge,
  Drawer,
  theme,
  message
} from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  KeyOutlined,
  SafetyOutlined,
  UserOutlined,
  BankOutlined,
  FilterOutlined,
  MailOutlined,
  TeamOutlined,
  LoginOutlined
} from '@ant-design/icons';
import { UserWithTenant } from '@/features/superadmin/types';
import { formatDate as formatDateUtil } from '@/utils/format';
import { getRoleLabel, getRoleOptions } from '@/features/auth/utils/roleLabels';
import { UserDialog } from './UserDialog';
import { DeleteUserDialogAdvanced } from './DeleteUserDialogAdvanced';
import { ResetPasswordDialog } from './ResetPasswordDialog';
import { supabase } from '@/integrations/supabase/client';
import { impersonationService } from '@/services/supabase/impersonationService';
import { useAuth } from '@/features/auth';

const { Text } = Typography;
const { useBreakpoint } = Grid;

export interface UserListProps {
  users: UserWithTenant[];
  onCreateUser: (userData: any) => Promise<any>;
  onUpdateUser: (userData: any) => Promise<any>;
  onDeleteUser: (userId: string, forceUnlink?: boolean) => Promise<any>;
  onCheckUserDependencies?: (userId: string) => Promise<any>;
  onResetPassword?: (userId: string, newPassword: string, permanent?: boolean) => Promise<any>;
  onRefresh?: () => void;
  tenants?: Array<{ id: string; nome: string }>;
}

const STATUS_CONFIG: Record<string, { label: string, color: string }> = {
  'ACTIVE': { label: 'Ativo', color: 'success' },
  'PENDING': { label: 'Pendente', color: 'warning' },
  'REJECTED': { label: 'Rejeitado', color: 'error' },
  'INACTIVE': { label: 'Inativo', color: 'default' }
};

const ROLES_COM_TIME = new Set(['LIDER_VENDAS', 'RELACIONADOR', 'SUPERVISOR']);

export function UserList({
  users,
  onCreateUser,
  onUpdateUser,
  onDeleteUser,
  onCheckUserDependencies,
  onResetPassword,
  onRefresh,
  tenants = []
}: UserListProps) {
  const { token } = theme.useToken();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { state } = useAuth();
  const currentUserUuid = state.user?.uuid;

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [roleFilter, setRoleFilter] = useState<string>('ALL');
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserWithTenant | null>(null);
  const [userDependencies, setUserDependencies] = useState<any>(null);
  const [isCheckingDependencies, setIsCheckingDependencies] = useState(false);
  const [supervisorByUserId, setSupervisorByUserId] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    let cancelled = false;

    const loadVinculos = async () => {
      const userIds = users.map((u) => u.id).filter(Boolean);
      if (userIds.length === 0) {
        setSupervisorByUserId(new Map());
        return;
      }

      const { data: vinculos, error } = await supabase
        .from('consultores')
        .select('user_id, supervisor_id')
        .in('user_id', userIds)
        .is('deleted_at', null);

      if (error || cancelled) {
        if (error) console.warn('Erro ao carregar vínculos de time:', error);
        return;
      }

      const supervisorIds = [
        ...new Set(
          (vinculos || [])
            .map((v) => v.supervisor_id)
            .filter((id): id is number => id != null)
        ),
      ];

      const nomeById = new Map<number, string>();
      if (supervisorIds.length > 0) {
        const { data: supervisores } = await supabase
          .from('consultores')
          .select('id, nome')
          .in('id', supervisorIds);

        for (const s of supervisores || []) {
          nomeById.set(Number(s.id), s.nome);
        }
      }

      const map = new Map<string, string>();
      for (const v of vinculos || []) {
        if (!v.user_id || v.supervisor_id == null) continue;
        const nome = nomeById.get(Number(v.supervisor_id));
        if (nome) map.set(v.user_id, nome);
      }

      if (!cancelled) setSupervisorByUserId(map);
    };

    loadVinculos();
    return () => {
      cancelled = true;
    };
  }, [users]);

  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const matchesSearch = searchTerm === '' ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.last_name?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'ALL' || user.status === statusFilter;
      const matchesRole = roleFilter === 'ALL' || user.role === roleFilter;

      return matchesSearch && matchesStatus && matchesRole;
    });
  }, [users, searchTerm, statusFilter, roleFilter]);

  const handleOpenEdit = (user: UserWithTenant) => {
    setSelectedUser(user);
    setIsUserModalOpen(true);
  };

  const handleOpenDelete = async (user: UserWithTenant) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);

    if (onCheckUserDependencies) {
      setIsCheckingDependencies(true);
      try {
        const deps = await onCheckUserDependencies(user.id);
        setUserDependencies(deps);
      } catch (error) {
        console.error('Erro ao verificar dependências:', error);
      } finally {
        setIsCheckingDependencies(false);
      }
    }
  };

  const handleOpenResetPassword = (user: UserWithTenant) => {
    setSelectedUser(user);
    setIsResetPasswordModalOpen(true);
  };

  const getUserInitials = (user: UserWithTenant) => {
    return `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase() || 'U';
  };

  const handleImpersonate = async (user: UserWithTenant) => {
    const hide = message.loading(`Entrando como ${user.first_name || user.username}…`, 0);
    try {
      await impersonationService.iniciar(user.id);
      // iniciar() faz reload total; hide roda só se algo falhar antes disso
    } catch (error) {
      hide();
      message.error(error instanceof Error ? error.message : 'Não foi possível personificar este usuário');
    }
  };

  const buildMenuItems = (user: UserWithTenant) => {
    const podeImpersonar = user.role !== 'SUPER_ADMIN' && user.id !== currentUserUuid;
    return [
      { key: 'edit', label: 'Editar', icon: <EditOutlined />, onClick: () => handleOpenEdit(user) },
      { key: 'password', label: 'Redefinir Senha', icon: <KeyOutlined />, onClick: () => handleOpenResetPassword(user) },
      ...(podeImpersonar
        ? [{ key: 'impersonate', label: 'Entrar como este usuário', icon: <LoginOutlined />, onClick: () => handleImpersonate(user) }]
        : []),
      { type: 'divider' as const },
      { key: 'delete', label: 'Excluir', icon: <DeleteOutlined />, danger: true, onClick: () => handleOpenDelete(user) },
    ];
  };

  const columns = [
    {
      title: 'Nome',
      key: 'name',
      render: (_: any, record: UserWithTenant) => (
        <Space>
          <Avatar
            style={{ backgroundColor: record.role === 'SUPER_ADMIN' ? token.colorWarning : token.colorPrimary }}
            icon={record.role === 'SUPER_ADMIN' ? <SafetyOutlined /> : <UserOutlined />}
          >
            {getUserInitials(record)}
          </Avatar>
          <Space orientation="vertical" size={0}>
            <Text strong>{record.first_name} {record.last_name}</Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>@{record.username}</Text>
          </Space>
        </Space>
      ),
    },
    {
      title: 'E-mail',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Empresa',
      key: 'tenant',
      render: (_: any, record: UserWithTenant) => (
        <Space>
          {record.tenant ? <BankOutlined style={{ color: token.colorPrimary }} /> : null}
          {record.tenant?.nome || <Text type="secondary">N/A</Text>}
        </Space>
      ),
    },
    {
      title: 'Perfil',
      key: 'role',
      render: (_: any, record: UserWithTenant) => (
        <Tag color={record.role === 'SUPER_ADMIN' ? 'gold' : record.role === 'ADMIN' ? 'blue' : 'default'}>
          {getRoleLabel(record.role)}
        </Tag>
      ),
    },
    {
      title: 'Time / Supervisor',
      key: 'time',
      render: (_: any, record: UserWithTenant) => {
        if (record.role === 'SUPERVISOR') {
          return (
            <Space size={4}>
              <TeamOutlined style={{ color: token.colorPrimary }} />
              <Text type="secondary">Lidera time</Text>
            </Space>
          );
        }
        if (!ROLES_COM_TIME.has(record.role)) {
          return <Text type="secondary">—</Text>;
        }
        const nome = supervisorByUserId.get(record.id);
        return nome ? (
          <Space size={4}>
            <TeamOutlined style={{ color: token.colorPrimary }} />
            <Text>{nome}</Text>
          </Space>
        ) : (
          <Text type="warning">Sem supervisor</Text>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, record: UserWithTenant) => {
        const config = STATUS_CONFIG[record.status] || { label: record.status, color: 'default' };
        return <Badge status={config.color as any} text={config.label} />;
      },
    },
    {
      title: 'Criado em',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => formatDateUtil(date),
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_: any, record: UserWithTenant) => (
        <Dropdown
          trigger={['click']}
          menu={{ items: buildMenuItems(record) }}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Barra de Ações Otimizada */}
      <div style={{ marginBottom: isMobile ? 12 : 16 }}>
        <Row gutter={[8, 8]} align="middle">
          <Col xs={isMobile ? 18 : 8} md={8}>
            <Input
              placeholder="Buscar membros..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              size={isMobile ? "middle" : "large"}
              style={{ borderRadius: 8, backgroundColor: token.colorBgBase, color: token.colorText }}
            />
          </Col>

          {isMobile ? (
            <Col xs={6} style={{ textAlign: 'right' }}>
              <Space size={4}>
                <Badge dot={statusFilter !== 'ALL' || roleFilter !== 'ALL'} color="#1890ff">
                  <Button
                    icon={<FilterOutlined />}
                    onClick={() => setIsFilterDrawerOpen(true)}
                  />
                </Badge>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setSelectedUser(null);
                    setIsUserModalOpen(true);
                  }}
                />
              </Space>
            </Col>
          ) : (
            <>
              <Col md={5}>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Status"
                  value={statusFilter}
                  onChange={setStatusFilter}
                  options={[
                    { value: 'ALL', label: 'Todos os Status' },
                    { value: 'ACTIVE', label: 'Ativo' },
                    { value: 'PENDING', label: 'Pendente' },
                    { value: 'REJECTED', label: 'Rejeitado' },
                  ]}
                  size="large"
                />
              </Col>
              <Col md={5}>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Perfil"
                  value={roleFilter}
                  onChange={setRoleFilter}
                  options={[
                    { value: 'ALL', label: 'Todos os Perfis' },
                    ...getRoleOptions(),
                  ]}
                  size="large"
                />
              </Col>
              <Col md={6} style={{ textAlign: 'right' }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setSelectedUser(null);
                    setIsUserModalOpen(true);
                  }}
                  size="large"
                >
                  Adicionar membro
                </Button>
              </Col>
            </>
          )}
        </Row>
      </div>

      {/* Drawer de Filtros Mobile */}
      <Drawer
        title="Filtrar Usuários"
        placement="bottom"
        onClose={() => setIsFilterDrawerOpen(false)}
        open={isFilterDrawerOpen}
        height="auto"
        styles={{ body: { paddingBottom: 40 } }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <div>
            <Text strong style={{ marginBottom: 8, display: 'block' }}>Status</Text>
            <Select
              style={{ width: '100%' }}
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: 'ALL', label: 'Todos os Status' },
                { value: 'ACTIVE', label: 'Ativo' },
                { value: 'PENDING', label: 'Pendente' },
                { value: 'REJECTED', label: 'Rejeitado' },
              ]}
            />
          </div>
          <div>
            <Text strong style={{ marginBottom: 8, display: 'block' }}>Perfil</Text>
            <Select
              style={{ width: '100%' }}
              value={roleFilter}
              onChange={setRoleFilter}
              options={[
                { value: 'ALL', label: 'Todos os Perfis' },
                ...getRoleOptions(),
              ]}
            />
          </div>
          <Button type="primary" block onClick={() => setIsFilterDrawerOpen(false)}>
            Aplicar Filtros
          </Button>
        </Space>
      </Drawer>

      <Card variant="borderless" styles={{ body: { padding: 0 } }}>
        {isMobile ? (
          <div style={{ padding: '0 16px' }}>
            {filteredUsers.length === 0 ? (
              <Empty description="Nenhum usuário encontrado" style={{ padding: '40px 0' }} />
            ) : (
              filteredUsers.map((user: UserWithTenant) => (
                <div
                  key={user.id}
                  style={{
                    padding: '12px',
                    marginBottom: '8px',
                    borderRadius: '12px',
                    backgroundColor: token.colorBgContainer,
                    border: `1px solid ${token.colorBorder}`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    boxShadow: token.boxShadow
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Space size={10}>
                      <Avatar
                        size={40}
                        style={{
                          backgroundColor: user.role === 'SUPER_ADMIN' ? token.colorWarning : token.colorPrimary,
                          fontSize: '14px'
                        }}
                        icon={user.role === 'SUPER_ADMIN' ? <SafetyOutlined /> : <UserOutlined />}
                      >
                        {getUserInitials(user)}
                      </Avatar>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <Text strong style={{ fontSize: '15px', lineHeight: '1.2' }}>{user.first_name} {user.last_name}</Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>@{user.username}</Text>
                      </div>
                    </Space>
                    <Dropdown
                      trigger={['click']}
                      menu={{ items: buildMenuItems(user) }}
                    >
                      <Button type="text" icon={<MoreOutlined style={{ fontSize: '20px' }} />} style={{ padding: '0 4px' }} />
                    </Dropdown>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 2 }}>
                    <Tag
                      color={user.role === 'SUPER_ADMIN' ? 'gold' : user.role === 'ADMIN' ? 'blue' : 'default'}
                      style={{ margin: 0, borderRadius: 4, height: 22, display: 'flex', alignItems: 'center' }}
                    >
                      {getRoleLabel(user.role)}
                    </Tag>
                    <Badge
                      status={(STATUS_CONFIG[user.status]?.color || 'default') as any}
                      text={<Text style={{ fontSize: '13px' }}>{STATUS_CONFIG[user.status]?.label || user.status}</Text>}
                    />
                  </div>

                  <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Text type="secondary" style={{ fontSize: '12px' }} ellipsis>
                      <MailOutlined style={{ marginRight: 6, fontSize: '10px' }} /> {user.email}
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px' }} ellipsis>
                      <BankOutlined style={{ marginRight: 6, fontSize: '10px' }} /> {user.tenant?.nome || 'Sem empresa'}
                    </Text>
                    {ROLES_COM_TIME.has(user.role) && (
                      <Text type="secondary" style={{ fontSize: '12px' }} ellipsis>
                        <TeamOutlined style={{ marginRight: 6, fontSize: '10px' }} />{' '}
                        {user.role === 'SUPERVISOR'
                          ? 'Lidera time'
                          : supervisorByUserId.get(user.id) || 'Sem supervisor'}
                      </Text>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={filteredUsers}
            rowKey="id"
            pagination={{ pageSize: 10, showSizeChanger: true }}
            locale={{ emptyText: <Empty description="Nenhum usuário cadastrado" /> }}
          />
        )}
      </Card>

      <UserDialog
        mode={selectedUser ? "edit" : "create"}
        open={isUserModalOpen}
        onOpenChange={setIsUserModalOpen}
        user={selectedUser as any}
        onSubmit={async (data) => {
          if (selectedUser) {
            await onUpdateUser({ id: selectedUser.id, ...data });
          } else {
            await onCreateUser(data);
          }
          onRefresh?.();
        }}
        tenants={tenants}
      />

      <DeleteUserDialogAdvanced
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        user={selectedUser}
        dependencies={userDependencies}
        isLoading={isCheckingDependencies}
        onConfirm={async (id, forceUnlink) => {
          await onDeleteUser(id, forceUnlink);
          setIsDeleteModalOpen(false);
          onRefresh?.();
        }}
      />

      <ResetPasswordDialog
        open={isResetPasswordModalOpen}
        onOpenChange={setIsResetPasswordModalOpen}
        user={selectedUser}
        onSubmit={async (userId, password, permanent) => {
          if (onResetPassword) {
            await onResetPassword(userId, password, permanent);
            setIsResetPasswordModalOpen(false);
          }
        }}
      />
    </div>
  );
}